/**
 * Phase 3.43 — Self-Service-Registrierung für Dienstleister (öffentlich).
 *
 * Erfasst Branche + Stammdaten und legt einen pending Eintrag im Dienstleister-
 * Verzeichnis an. Admin muss vor Veröffentlichung freigeben.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { CheckCircle2, Handshake, Send, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ServiceProviderRegisterPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [branches, setBranches] = useState([]);
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        name: "", branch: searchParams.get("branch") || "",
        contact_first_name: "", contact_last_name: "",
        contact_email: "", contact_phone: "",
        password: "",
        website: "",
        street: "", postal_code: "", city: "", country: "Deutschland",
        regions: "",
        description: "",
        price_from_eur: "",
        whatsapp_consent: false,
    });

    useEffect(() => {
        api.get("/service-providers/branches")
            .then((r) => setBranches(r.data.items || []))
            .catch(() => setBranches([]));
    }, []);

    const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async () => {
        if (!form.name.trim() || form.name.trim().length < 2) { toast.error("Bitte Firmennamen eingeben"); return; }
        if (!form.branch) { toast.error("Bitte Branche auswählen"); return; }
        if (!form.contact_email.trim() || !form.contact_email.includes("@")) { toast.error("Bitte gültige E-Mail eingeben"); return; }
        if (!form.password || form.password.length < 8) { toast.error("Bitte Passwort mit mindestens 8 Zeichen wählen"); return; }
        setBusy(true);
        try {
            const payload = {
                ...form,
                regions: form.regions.split(",").map((s) => s.trim()).filter(Boolean),
                price_from_eur: form.price_from_eur ? parseFloat(form.price_from_eur) : null,
            };
            await api.post("/service-providers/register", payload);
            setDone(true);
        } catch (e) {
            const detail = e.response?.data?.detail;
            let msg = "Registrierung fehlgeschlagen";
            if (typeof detail === "string") {
                msg = detail;
            } else if (Array.isArray(detail) && detail.length > 0) {
                // Pydantic validation errors → ersten Fehler anzeigen
                const first = detail[0];
                const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : "Feld";
                msg = `${field}: ${first.msg || "ungültig"}`;
            }
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="sp-register-success">
                <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
                <h1 className="font-display font-black text-3xl mb-2">Registrierung eingereicht!</h1>
                <p className="text-gray-600 mb-6">
                    Ihre Registrierung wurde erfolgreich übermittelt. Unser Team prüft die Angaben und schaltet Ihren Eintrag nach Freigabe für alle Händler frei.
                </p>
                <p className="text-sm text-gray-500 mb-6">Sie können sich nun mit Ihrer E-Mail und dem gewählten Passwort als <strong>Dienstleister</strong> einloggen, um Ihr Profil zu pflegen.</p>
                <div className="flex gap-2 justify-center flex-wrap">
                    <Button onClick={() => navigate("/login?role=service_provider")} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider" data-testid="sp-register-login-btn">
                        Jetzt einloggen
                    </Button>
                    <Button onClick={() => navigate("/")} variant="outline" className="font-bold rounded-sm uppercase tracking-wider" data-testid="sp-register-home-btn">
                        Zur Startseite
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-8" data-testid="sp-register-page">
            <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-600 hover:text-[#111827] mb-3">
                <ArrowLeft className="w-3.5 h-3.5" /> Zurück
            </Link>
            <div className="mb-6">
                <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] flex items-center gap-2 mb-1">
                    <Handshake className="w-3.5 h-3.5" /> Dienstleister- / Lieferanten-Registrierung
                </div>
                <h1 className="font-display text-3xl md:text-4xl font-black">Werden Sie Teil unseres Dienstleister- &amp; Lieferanten-Netzwerks</h1>
                <p className="text-gray-600 text-sm mt-2 max-w-2xl">
                    Registrieren Sie Ihr Unternehmen, damit Zweiradhändler auf Sie zugreifen können —
                    von Hol-/Bringservice über Zulassung bis Finanzierung.
                </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-sm p-6 space-y-5">
                <div>
                    <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Branche *</Label>
                    <select
                        value={form.branch}
                        onChange={(e) => upd("branch", e.target.value)}
                        className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 bg-white text-sm"
                        data-testid="sp-register-branch"
                    >
                        <option value="">— bitte wählen —</option>
                        {branches.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
                    </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Firmenname *</Label>
                        <Input value={form.name} onChange={(e) => upd("name", e.target.value)} className="rounded-sm mt-1" data-testid="sp-register-name" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Website</Label>
                        <Input type="url" value={form.website} onChange={(e) => upd("website", e.target.value)} placeholder="https://..." className="rounded-sm mt-1 font-mono-num text-sm" data-testid="sp-register-website" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Ansprechpartner Vorname</Label>
                        <Input value={form.contact_first_name} onChange={(e) => upd("contact_first_name", e.target.value)} className="rounded-sm mt-1" data-testid="sp-register-firstname" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Ansprechpartner Nachname</Label>
                        <Input value={form.contact_last_name} onChange={(e) => upd("contact_last_name", e.target.value)} className="rounded-sm mt-1" data-testid="sp-register-lastname" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">E-Mail *</Label>
                        <Input type="email" value={form.contact_email} onChange={(e) => upd("contact_email", e.target.value)} className="rounded-sm mt-1" data-testid="sp-register-email" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Passwort * (mind. 8 Zeichen)</Label>
                        <Input type="password" value={form.password} onChange={(e) => upd("password", e.target.value)} className="rounded-sm mt-1 font-mono-num" data-testid="sp-register-password" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Telefon</Label>
                        <Input value={form.contact_phone} onChange={(e) => upd("contact_phone", e.target.value)} placeholder="+49 …" className="rounded-sm mt-1 font-mono-num" data-testid="sp-register-phone" />
                        {form.contact_phone.trim() && (
                            <label className="flex items-start gap-2 mt-2 cursor-pointer" data-testid="sp-whatsapp-consent-row">
                                <input
                                    type="checkbox"
                                    checked={!!form.whatsapp_consent}
                                    onChange={(e) => upd("whatsapp_consent", e.target.checked)}
                                    className="mt-0.5 w-4 h-4 accent-[#25D366] shrink-0"
                                    data-testid="sp-whatsapp-consent-checkbox"
                                />
                                <span className="text-[11px] text-gray-600 leading-snug">
                                    Kontaktaufnahme per <span className="font-bold text-[#128C7E]">WhatsApp</span> unter dieser Nummer erlaubt. (Optional, jederzeit widerrufbar.)
                                </span>
                            </label>
                        )}
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Straße & Hausnr.</Label>
                        <Input value={form.street} onChange={(e) => upd("street", e.target.value)} className="rounded-sm mt-1" data-testid="sp-register-street" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">PLZ</Label>
                        <Input value={form.postal_code} onChange={(e) => upd("postal_code", e.target.value)} className="rounded-sm mt-1 font-mono-num" data-testid="sp-register-postal" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Ort</Label>
                        <Input value={form.city} onChange={(e) => upd("city", e.target.value)} className="rounded-sm mt-1" data-testid="sp-register-city" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Land</Label>
                        <Input value={form.country} onChange={(e) => upd("country", e.target.value)} className="rounded-sm mt-1" data-testid="sp-register-country" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Einzugsgebiet (PLZ-Präfixe, kommasepariert)</Label>
                        <Input value={form.regions} onChange={(e) => upd("regions", e.target.value)} placeholder="10, 11, 12" className="rounded-sm mt-1 font-mono-num text-sm" data-testid="sp-register-regions" />
                    </div>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Beschreibung</Label>
                    <Textarea value={form.description} onChange={(e) => upd("description", e.target.value)} rows={4} placeholder="Welche Leistungen bieten Sie an?" className="rounded-sm mt-1 text-sm" data-testid="sp-register-description" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Preise ab (€, optional)</Label>
                    <Input type="number" min="0" step="1" value={form.price_from_eur} onChange={(e) => upd("price_from_eur", e.target.value)} className="rounded-sm mt-1 font-mono-num w-40" data-testid="sp-register-price" />
                </div>
                <div className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-sm p-3">
                    Hinweis: Ihre Registrierung wird vor der Veröffentlichung manuell durch unser Team geprüft.
                </div>
                <Button
                    onClick={submit}
                    disabled={busy}
                    className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider"
                    data-testid="sp-register-submit"
                >
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Registrierung absenden
                </Button>
            </div>
        </div>
    );
}
